import { useState, useRef, useEffect } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { AlertCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';

interface VerifyState {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  referralCode?: string;
}

export default function VerifyEmailPage() {
  const { user, register, resendVerifyOTP } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const verifyState = location.state as VerifyState | null;

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // If already logged in, redirect home
  if (user) return <Navigate to="/" replace />;

  // If no registration data was passed, redirect to signup
  if (!verifyState) return <Navigate to="/signup" replace />;

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) newOtp[i] = pasted[i] || '';
    setOtp(newOtp);
    const focusIdx = Math.min(pasted.length, 5);
    otpRefs.current[focusIdx]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) { setError('Please enter the 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      await register({
        email: verifyState.email,
        password: verifyState.password,
        first_name: verifyState.firstName,
        last_name: verifyState.lastName,
        phone: verifyState.phone,
        otp: code,
        referral_code: verifyState.referralCode,
      });
      // After register, user state updates → Navigate component above will redirect
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await resendVerifyOTP(verifyState.email);
      setResendCooldown(60);
      setError('');
    } catch {
      setError('Failed to resend OTP');
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO {...pageSeoMeta.verifyEmail} />
      <div className={`w-full max-w-md rounded-2xl shadow-xl p-8 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}>

        <Link to="/signup" className={`inline-flex items-center gap-1 text-xs mb-6 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
          <ArrowLeft size={14} /> Back to Sign Up
        </Link>

        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-blue-500 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="text-white" size={24} />
          </div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Verify Your Email</h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            We sent a 6-digit code to {verifyState.email}
          </p>
        </div>

        <div className={`flex items-start gap-2 p-3 rounded-lg mb-4 text-sm ${isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>Can't find the email? Check your <strong>Spam</strong> or <strong>Junk</strong> folder. It may take a minute to arrive.</span>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <div className="space-y-5">
          <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => { otpRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleOtpChange(i, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(i, e)}
                className={`w-12 h-14 text-center text-xl font-bold rounded-xl border outline-none transition-colors ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 text-white focus:border-emerald-500'
                    : 'bg-white border-gray-200 text-gray-900 focus:border-emerald-500'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleVerify}
            disabled={loading || otp.join('').length !== 6}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
          >
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </button>

          <div className="text-center">
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Didn't receive the code?{' '}
              {resendCooldown > 0 ? (
                <span className="text-gray-500">Resend in {resendCooldown}s</span>
              ) : (
                <button onClick={handleResend} className="text-emerald-500 font-medium hover:underline">
                  Resend OTP
                </button>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
